import type { ColumnDef } from "../columns";

/**
 * Pluggable formatter bundle. The table engine accepts an injected bundle
 * so that consumers can route to `@aio/platform-i18n` (production) or to
 * mocks (tests) without this module reaching across package boundaries.
 *
 * The shape mirrors `formatNumber` / `formatCurrency` / `formatDate` from
 * `@aio/platform-i18n` but uses plain `string` for `locale` to avoid a
 * circular type dependency.
 */
export interface FormatterBundle {
  number: (locale: string, value: number, options?: Intl.NumberFormatOptions) => string;
  currency: (locale: string, value: number, currency: string) => string;
  date: (locale: string, value: Date, options?: Intl.DateTimeFormatOptions) => string;
}

/** Display string used for null/undefined date-like cells. */
export const EMPTY_DATE_DISPLAY = "—";

/**
 * Dispatch table for converting a raw cell value into a display string,
 * keyed off the column's declared `type`. Returns `""` for `type: "custom"`
 * since custom columns own their rendering via `column.cell`.
 *
 * Resolution order:
 *   1. If `column.format` is provided, call it and return.
 *   2. Otherwise dispatch on `column.type`.
 *
 * Booleans intentionally render as `"Yes"` / `"No"` (no emoji glyphs).
 * Production callers route the strings through their i18n layer; this
 * module returns the English defaults.
 */
export function formatCell<TRow>(
  value: unknown,
  column: ColumnDef<TRow>,
  locale: string,
  formatters: FormatterBundle,
  extras?: { row?: TRow },
): string {
  if (column.format) {
    return column.format({
      row: extras?.row as TRow,
      value,
      column,
      locale,
    });
  }

  switch (column.type) {
    case "text":
      if (value === null || value === undefined) return "";
      return String(value);

    case "number":
      if (value === null || value === undefined) return "";
      if (typeof value !== "number") {
        throw new Error(
          `Column "${column.key}" of type "number" received non-number value: ${typeof value}`,
        );
      }
      return formatters.number(locale, value);

    case "money": {
      if (value === null || value === undefined) return "";
      if (typeof value !== "number") {
        throw new Error(
          `Column "${column.key}" of type "money" received non-number value: ${typeof value}`,
        );
      }
      const currency = column.currency ?? "EUR";
      return formatters.currency(locale, value, currency);
    }

    case "percent":
      if (value === null || value === undefined) return "";
      if (typeof value !== "number") {
        throw new Error(
          `Column "${column.key}" of type "percent" received non-number value: ${typeof value}`,
        );
      }
      return formatters.number(locale, value, { style: "percent" });

    case "date":
      if (value === null || value === undefined) return EMPTY_DATE_DISPLAY;
      if (!(value instanceof Date)) {
        throw new Error(
          `Column "${column.key}" of type "date" received non-Date value`,
        );
      }
      return formatters.date(locale, value);

    case "datetime":
      if (value === null || value === undefined) return EMPTY_DATE_DISPLAY;
      if (!(value instanceof Date)) {
        throw new Error(
          `Column "${column.key}" of type "datetime" received non-Date value`,
        );
      }
      return formatters.date(locale, value, { dateStyle: "medium", timeStyle: "short" });

    case "enum": {
      if (value === null || value === undefined) return "";
      const key = String(value);
      return column.enumLabels?.[key] ?? key;
    }

    case "boolean":
      // Yes / No — English defaults; no emoji glyphs (per platform style rules).
      // Callers wanting localized labels should set column.format.
      if (value === null || value === undefined) return "";
      return value ? "Yes" : "No";

    case "relation":
      if (value === null || value === undefined) return "";
      if (column.relationLabel) return column.relationLabel(value);
      return String(value);

    case "custom":
      // Custom columns are rendered via column.cell; formatCell has nothing
      // to contribute. Exporters should also rely on column.format for these.
      return "";
  }
}
